/**
 * AIWARI — WoW-WotLK 3.3.5a-style faction + race + class system.
 *
 * Two factions (Alliance / Horde), 8 races, each race opens a curated
 * subset of the 6 base classes. Modelled directly on WoW WotLK 3.3.5a 1.12
 * for player familiarity. Names reused 1:1 in Russian where there's a
 * canonical Russian translation; otherwise a sensible RU fallback.
 *
 * Replaces the older Three-Winds (Tidal/Rooted/Anchorless) faction
 * model. We keep the `side` schema field but reuse the values
 * 'alliance' / 'horde' so the existing realm pipeline doesn't have to
 * be torn out — see WOW_TO_LEGACY_SIDE for the bridge.
 */

import type { ClassId } from './classes.js';

// R232q5w: 'outcast' = Одичалые (third heraldic faction). Server stores
// `faction` as a string column so adding a new value is forward-compatible.
export type Faction = 'alliance' | 'horde' | 'outcast';

export type RaceId =
  // Alliance
  | 'human'
  | 'dwarf'
  | 'night-elf'
  | 'gnome'
  // Horde
  | 'orc'
  | 'undead'
  | 'tauren'
  | 'troll'
  // Outcast — Одичалые (R232q5w)
  | 'goblin'
  | 'half-blood'
  | 'shadowkin';

export interface RaceDef {
  id: RaceId;
  faction: Faction;
  name: { ru: string; en: string };
  /** Two-line lore blurb shown on race-pick. */
  blurb: { ru: string; en: string };
  /** Capital city — starter zone. */
  capital: { ru: string; en: string };
  /** Allowed classes for this race (WotLK 3.3.5a-accurate). */
  classes: ClassId[];
  /** Visual: which KayKit base body model to load by default. */
  kaykitBody: 'human' | 'dwarf' | 'elf' | 'gnome' | 'orc' | 'undead' | 'tauren' | 'troll';
  /** Theme colour for race accents in the picker UI. */
  colour: string;
  /** Suggested gender list — both 'male' and 'female' for all races (KayKit cosmetic only). */
  genders: ['male', 'female'];
  /** Default skin-tone palette as hex strings, used by the customiser. */
  skinTones: string[];
  /** Default hair colour palette. */
  hairColours: string[];
}

/**
 * R232q5h T4.6: per-race stat modifiers — applied additively on top of
 * the class baseline. Vanilla-inspired but tuned to AIWARI's lower stat
 * scale (HP base 200, pow base 10).
 *
 *   hp:      additive bonus to hpMax (small flat boost)
 *   pow:     additive spell/attack power
 *   armor:   additive armor base
 *   crit:    +% crit chance (capped 100)
 *   haste:   +% haste
 *   resist:  +% generic damage reduction
 *
 * Example: dwarves get +25 hp + 2 armor (sturdy), gnomes +5 pow +1 crit
 * (clever), trolls +1 haste (frenzy). Differences are deliberately mild so
 * race feels like a flavour pick, not a min-max trap.
 */
export interface RaceStats {
  hp?: number;
  pow?: number;
  armor?: number;
  crit?: number;
  haste?: number;
  resist?: number;
}

export const RACE_STATS: Record<RaceId, RaceStats> = {
  human:       { hp: 10, pow: 1 },
  dwarf:       { hp: 25, armor: 2 },
  'night-elf': { haste: 1, resist: 1 },
  gnome:       { pow: 5, crit: 1 },
  orc:         { hp: 15, pow: 2 },
  undead:      { hp: 5, resist: 2 },
  tauren:      { hp: 35 },
  troll:       { pow: 1, haste: 2 },
  // R232q5w: Outcast — slightly higher crit and haste, less raw HP (rogue-feel).
  goblin:      { pow: 3, crit: 2, haste: 1 },
  'half-blood':{ hp: 10, pow: 2, resist: 1 },
  shadowkin:   { haste: 2, crit: 2 },
};

// R232q5w: AIWARI canonical factions. UI text is renamed:
//   alliance → Орден Серебряной Зари
//   horde    → Бурное Племя
//   outcast  → Одичалые
// `colour` keys map to lobby palette: alliance-blue, horde-red, arcane purple.
export const FACTIONS: Record<Faction, { name: { ru: string; en: string }; blurb: { ru: string; en: string }; colour: string }> = {
  alliance: {
    name: { ru: 'Орден Серебряной Зари', en: 'Order of the Silver Dawn' },
    blurb: {
      ru: 'Союз людей, дворфов, ночных эльфов и гномов. За справедливость, традиции и свет.',
      en: 'Union of humans, dwarves, night elves, and gnomes. For justice, tradition, and light.',
    },
    colour: '#3E6FA5',
  },
  horde: {
    name: { ru: 'Бурное Племя', en: 'Storm Tribe' },
    blurb: {
      ru: 'Орки, нежить, таурены и тролли. За свободу, силу, и кровь предков.',
      en: 'Orcs, undead, tauren, and trolls. For freedom, strength, and the blood of ancestors.',
    },
    colour: '#8B2828',
  },
  outcast: {
    name: { ru: 'Одичалые', en: 'Outcasts' },
    blurb: {
      ru: 'Изгои, отвергшие знамёна Ордена и Племени. Гоблины, полукровки и теневой народ — третья сила Стормвейла, идущая своим путём.',
      en: 'Exiles who rejected both Order and Tribe. Goblins, half-bloods, and shadowkin — a third path under no banner.',
    },
    colour: '#6B3F8C',
  },
};

export const RACES: RaceDef[] = [
  // ── ALLIANCE ────────────────────────────────────────────────────
  {
    id: 'human',
    faction: 'alliance',
    name: { ru: 'Велион', en: 'Human' },
    blurb: {
      ru: 'Гибкие универсалы. Хороши во всём, мастера ни в чём — но именно люди держат Серебряную Зарю.',
      en: 'Flexible generalists. Good at everything, masters of nothing — yet humans hold the Silver Dawn together.',
    },
    capital: { ru: 'Львиная Твердыня', en: 'Lionhold' },
    classes: ['warrior', 'paladin', 'rogue', 'cleric', 'mage', 'warlock'],
    kaykitBody: 'human',
    colour: '#60A5FA',
    genders: ['male', 'female'],
    skinTones: ['#F1C7A8', '#E2A77F', '#C58860', '#9C6843', '#6F4628'],
    hairColours: ['#3B2412', '#6B4226', '#9A6B3F', '#C7A26F', '#E8D6B0', '#1F1A14'],
  },
  {
    id: 'dwarf',
    faction: 'alliance',
    name: { ru: 'Дургар', en: 'Dwarf' },
    blurb: {
      ru: 'Невысокие, но крепкие как камень. Кузнецы, охотники, мастера выпивки.',
      en: 'Short but stone-tough. Smiths, hunters, masters of drink.',
    },
    capital: { ru: 'Железный Костёр', en: 'IronPyre' },
    classes: ['warrior', 'paladin', 'ranger', 'rogue', 'cleric'],
    kaykitBody: 'dwarf',
    colour: '#A16207',
    genders: ['male', 'female'],
    skinTones: ['#E8C7A4', '#CCA37A', '#A57E58', '#7E5B3D'],
    hairColours: ['#8B4513', '#A0522D', '#D4A55F', '#E8D6B0', '#C84B2C', '#4A2C18'],
  },
  {
    id: 'night-elf',
    faction: 'alliance',
    name: { ru: 'Селенар', en: 'Night Elf' },
    blurb: {
      ru: 'Древние стражи леса. Тысячелетние охотники, друиды, лучники с фиолетовой кожей.',
      en: 'Ancient forest guardians. Millennia-old hunters, druids, archers with violet skin.',
    },
    capital: { ru: 'Лунная Ива', en: 'Moonwillow' },
    classes: ['warrior', 'ranger', 'rogue', 'cleric', 'druid'],
    kaykitBody: 'elf',
    colour: '#A78BFA',
    genders: ['male', 'female'],
    skinTones: ['#B59CD9', '#9B7CC4', '#7C5BAE', '#5C3D8C'],
    hairColours: ['#2C1A3E', '#5B3F7A', '#9173B0', '#C4B0DC', '#E0D2EC', '#FFFFFF'],
  },
  {
    id: 'gnome',
    faction: 'alliance',
    name: { ru: 'Когран', en: 'Gnome' },
    blurb: {
      ru: 'Изобретатели и инженеры. Маленькие, шумные, опасно умные.',
      en: 'Inventors and engineers. Small, loud, dangerously clever.',
    },
    capital: { ru: 'Шестерённый Город', en: 'Cogwarren' },
    classes: ['warrior', 'rogue', 'mage', 'warlock'],
    kaykitBody: 'gnome',
    colour: '#F472B6',
    genders: ['male', 'female'],
    skinTones: ['#F2D6BD', '#D9B698', '#B39173'],
    hairColours: ['#FF6B9D', '#5BC0EB', '#9CFFB3', '#FF9A4D', '#E0D6F0', '#3B2412'],
  },

  // ── HORDE ───────────────────────────────────────────────────────
  {
    id: 'orc',
    faction: 'horde',
    name: { ru: 'Грумак', en: 'Orc' },
    blurb: {
      ru: 'Зеленокожие воины с холодным сердцем и горячей кровью. Сила и честь.',
      en: 'Green-skinned warriors with cold hearts and hot blood. Strength and honour.',
    },
    capital: { ru: 'Цитадель Бранного Колеса', en: 'Wartus Citadel' },
    classes: ['warrior', 'ranger', 'rogue', 'shaman', 'warlock'],
    kaykitBody: 'orc',
    colour: '#65A30D',
    genders: ['male', 'female'],
    skinTones: ['#7CB342', '#558B2F', '#33691E', '#1B5E20'],
    hairColours: ['#1A1A1A', '#3E2723', '#5D4037', '#8D6E63', '#A1887F'],
  },
  {
    id: 'undead',
    faction: 'horde',
    name: { ru: 'Морвен', en: 'Undead' },
    blurb: {
      ru: 'Свободные мертвецы. Ходячие трупы, освободившиеся от воли Короля-лича.',
      en: 'Free dead. Walking corpses freed from the Lich King\'s grip.',
    },
    capital: { ru: 'Тёмный Склеп', en: 'Crypthold' },
    classes: ['warrior', 'rogue', 'cleric', 'mage', 'warlock'],
    kaykitBody: 'undead',
    colour: '#84CC16',
    genders: ['male', 'female'],
    skinTones: ['#B0BEC5', '#90A4AE', '#78909C', '#607D8B'],
    hairColours: ['#1A1A1A', '#37474F', '#546E7A', '#9E9E9E', '#CFD8DC', '#FFFFFF'],
  },
  {
    id: 'tauren',
    faction: 'horde',
    name: { ru: 'Гартан', en: 'Tauren' },
    blurb: {
      ru: 'Гигантские быкоподобные кочевники. Земля помнит их предков, небо знает их голоса.',
      en: 'Giant bovine nomads. The earth remembers their ancestors, the sky knows their voices.',
    },
    capital: { ru: 'Грозовой Утёс', en: 'Stormcliff' },
    classes: ['warrior', 'ranger', 'shaman', 'druid'],
    kaykitBody: 'tauren',
    colour: '#92400E',
    genders: ['male', 'female'],
    skinTones: ['#8D6E63', '#6D4C41', '#4E342E', '#3E2723', '#FFE0B2'],
    hairColours: ['#3E2723', '#5D4037', '#795548', '#A1887F', '#1A1A1A'],
  },
  {
    id: 'troll',
    faction: 'horde',
    name: { ru: 'Накар', en: 'Troll' },
    blurb: {
      ru: 'Дикие охотники джунглей. Регенерация, метание копий, древняя магия вуду.',
      en: 'Wild jungle hunters. Regeneration, spear-throwing, ancient voodoo magic.',
    },
    capital: { ru: 'Сен\'джин', en: 'Sen\'jin' },
    classes: ['warrior', 'ranger', 'rogue', 'cleric', 'mage', 'shaman'],
    kaykitBody: 'troll',
    colour: '#06B6D4',
    genders: ['male', 'female'],
    skinTones: ['#5BC0EB', '#3E94C7', '#2563EB', '#1E40AF'],
    hairColours: ['#FF1744', '#E91E63', '#9C27B0', '#3F51B5', '#1A1A1A'],
  },

  // ── OUTCAST — Одичалые (R232q5w) ────────────────────────────────
  {
    id: 'goblin',
    faction: 'outcast',
    name: { ru: 'Гриккс', en: 'Goblin' },
    blurb: {
      ru: 'Хитрые торгаши с воровскими руками и шестерёнками в карманах. Видят выгоду там, где другие видят руины.',
      en: 'Cunning traders with thieving hands and gears in their pockets. See profit where others see ruins.',
    },
    capital: { ru: 'Шёпотный Базар', en: 'Whispermarket' },
    classes: ['warrior', 'rogue', 'mage', 'warlock', 'shaman'],
    kaykitBody: 'gnome',
    colour: '#A37430',
    genders: ['male', 'female'],
    skinTones: ['#9CB05A', '#7E9442', '#5E742E', '#A07640'],
    hairColours: ['#3B2412', '#6B4226', '#A33232', '#1F1A14', '#C9A96A'],
  },
  {
    id: 'half-blood',
    faction: 'outcast',
    name: { ru: 'Ваэрин', en: 'Half-Blood' },
    blurb: {
      ru: 'Дитя двух народов, не принятое ни одним. Сила Бурного Племени, выправка Ордена — и горький дар одиночества.',
      en: 'Child of two peoples, claimed by neither. Tribe\'s strength, Order\'s bearing, and the bitter gift of solitude.',
    },
    capital: { ru: 'Перекрёстье Изгоев', en: 'Outcast Crossing' },
    classes: ['warrior', 'paladin', 'ranger', 'rogue', 'cleric', 'shaman', 'druid'],
    kaykitBody: 'human',
    colour: '#8B6F4E',
    genders: ['male', 'female'],
    skinTones: ['#C58860', '#9C6843', '#7A5036', '#A37752', '#6B4F37'],
    hairColours: ['#3B2412', '#6B4226', '#9A6B3F', '#1F1A14', '#A33232'],
  },
  {
    id: 'shadowkin',
    faction: 'outcast',
    name: { ru: 'Шейдар', en: 'Shadowkin' },
    blurb: {
      ru: 'Народ изгнанных эльфийских колдунов. Кожа цвета сумерек, глаза — фиалкового пламени. Владеют запретной магией грани.',
      en: 'Exiled elven sorcerers. Skin the colour of dusk, eyes of violet flame. Wielders of forbidden veil-magic.',
    },
    capital: { ru: 'Тенесвет', en: 'Shadowveil' },
    classes: ['warrior', 'rogue', 'cleric', 'mage', 'warlock', 'druid'],
    kaykitBody: 'elf',
    colour: '#6B3F8C',
    genders: ['male', 'female'],
    skinTones: ['#6B5A78', '#4F4060', '#3A2F4A', '#7E6E92'],
    hairColours: ['#2C1A3E', '#5B3F7A', '#9173B0', '#1A1A1A', '#FFFFFF'],
  },
];

export function racesByFaction(f: Faction): RaceDef[] {
  return RACES.filter((r) => r.faction === f);
}

export function raceById(id: string): RaceDef | undefined {
  return RACES.find((r) => r.id === id);
}

export function classAllowedForRace(raceId: RaceId, classId: ClassId): boolean {
  const race = raceById(raceId);
  if (!race) return false;
  return race.classes.includes(classId);
}

/** Bridge legacy `side` (tidal/rooted/anchorless) to/from new factions.
 *  Existing characters created before R55 keep their old side; on next
 *  login they're migrated to the new model. */
export const LEGACY_SIDE_TO_FACTION: Record<string, Faction> = {
  tidal: 'alliance',
  rooted: 'alliance',
  anchorless: 'horde',
  alliance: 'alliance',
  horde: 'horde',
};
